import React, { useRef, useState, useEffect, FC } from "react";
import { DL_FILTER_EVENT, DL_FILTER_ACTION_ON } from "helpers/constants";
import { datalayerWrapper } from "lib/analytics/datalayerHelpers";
import styled from "styled-components";
import Styles from "./styles.scss";

interface FilterInputProps extends React.HTMLProps<HTMLInputElement> {
    datalayerKey: string;
    filterId: string;
    handleChange: (filterId: string, value: string[]) => void;
    value: string[];
    ariaLabel?: string;
    classes?: string;
    dataTestId?: string;
    defaultValue?: string[];
    errorText?: string;
    helpText?: string;
    isValidInput?: (value: string) => boolean;
    withPrefix?: boolean;
    numeric?: boolean;
}

const Prefix = styled.div<{ show: boolean }>`
    align-items: center;
    display: ${({ show }) => (show ? "flex" : "none")};
    font-size: 1rem;
    font-weight: 400;
    height: 44px;
    left: 0.875rem;
    pointer-events: none;
    position: absolute;
`;

export const FilterInput: FC<FilterInputProps> = ({
    helpText = "",
    value,
    classes = "",
    defaultValue = [],
    filterId,
    datalayerKey,
    handleChange,
    errorText,
    isValidInput,
    dataTestId = "filter-input",
    ariaLabel = "",
    withPrefix,
    numeric,
    ...props
}) => {
    const anchorRef = React.createRef<HTMLDivElement>();
    const textInput = useRef<HTMLInputElement>(null);

    const [inputValue, setInputValue] = useState(
        defaultValue && defaultValue.length ? defaultValue[0] : ""
    );
    const [showPrefix, setShowPrefix] = useState(false);

    useEffect(() => {
        setInputValue(Array.isArray(value) && value.length > 0 ? value[0] : "");
    }, [value]);

    const getValueToUpdate = (priceValue: string | number): string[] => {
        const stringValue = String(priceValue);
        if (stringValue.length > 0) {
            return [stringValue];
        }

        return [];
    };

    const handleKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (textInput.current) {
            textInput.current.classList.remove("error");

            if (e.keyCode === 13) {
                textInput.current.blur();
            }
        }
    };

    const handleShowPrefix = () => {
        if (withPrefix && !inputValue) setShowPrefix(!showPrefix);
    };

    const handleBlur = () => {
        if (!textInput.current) {
            return;
        }

        if (typeof isValidInput === "function" && !isValidInput(inputValue)) {
            textInput.current.classList.add("error");
            return;
        }
        textInput.current.classList.remove("error");

        datalayerWrapper({
            event: DL_FILTER_EVENT,
            filter_action: DL_FILTER_ACTION_ON,
            filter_name: datalayerKey,
            filter_value: typeof inputValue === "string" ? inputValue.toLowerCase() : ""
        });

        handleChange(filterId, getValueToUpdate(inputValue));
        handleShowPrefix();
    };

    return (
        <div
            className={`filter__text-input ${classes} ${inputValue ? "selection" : "no-selection"}`}
            data-testid={dataTestId}
        >
            <style jsx>{Styles}</style>
            <div ref={anchorRef} className="filter__y-scroll-anchor" />
            <Prefix show={showPrefix}>€</Prefix>
            <input
                key={filterId}
                ref={textInput}
                value={inputValue}
                type="text"
                {...(numeric && { inputMode: "numeric" })}
                onFocus={handleShowPrefix}
                onBlur={handleBlur}
                onKeyUp={handleKeyUp}
                onChange={(e) => setInputValue(e.target.value)}
                data-testid={`${filterId}`}
                aria-label={ariaLabel}
                {...props}
            />
            <span className="help-text">{`${helpText}`}</span>
            <div className="error-text">{errorText}</div>
        </div>
    );
};

FilterInput.displayName = "FilterInput";
